// v49.1 — Auth context + useShellUser hook.
// Single source of truth for "who is signed in" across the app. Wraps
// the Supabase session, derives the canonical ShellUser shape, and
// exposes the three mutation primitives (signIn, signUp, signOut).
//
// The shellUser shape mirrors what v48.x components expect to read from
// localStorage["lc_subscription"]: { name, email, role, company, mailId }.
// This means feature components migrated from v48 can keep reading the
// same fields — they just consume `useShellUser()` instead.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@/lib/api/auth";

// Compat: User is just the session user shape
type User = Session["user"];
import { supabase } from "@/lib/api/supabase-client";
import { LABOURCORE_API } from "@/lib/api";
import type { AdminTier, Profile, Role } from "@/lib/api/types";
import { shellSignOut } from "./helpers";
import { primeMatchModel } from "@/features/partners/matchModel";
import { primeMatchWeights } from "@/features/partners/matchWeights";

export interface ShellUser {
  id:          string;
  email:       string;
  name:        string;
  role:        Role;
  company:     string;
  companyId:   string | null;
  mailId:      string;
  initials:    string;
  gdprAccepted: boolean;
  avatarUrl?:  string | null;
  coverUrl?:   string | null;
  onboardingComplete: boolean;
  isAdmin: boolean;
  /* v50.321 — platform-admin tier. master = full panel (also isAdmin),
     board = view-only oversight, investor = revenue overview only.
     null for normal users. Legacy is_admin-without-role maps to master. */
  adminRole: AdminTier | null;
  /* v50.72 — SaaS tier for the user's company. Gates bulk-export and
     other Pro-only features. Admins always behave as Pro regardless
     (see useIsProTier). Single-record export remains free for all
     tiers per GDPR Art. 15(3). */
  subscriptionTier: "free" | "pro";
}

interface AuthState {
  status:        "loading" | "authenticated" | "anonymous";
  user:          ShellUser | null;
  rawSession:    Session | null;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ ok: boolean; error?: string; resetRequired?: boolean; resetToken?: string; resetUser?: { id: string; email: string } }>;
  signUp: (args: {
    email:     string;
    password:  string;
    name:      string;
    role:      Role;
    company:   string;
    extras?:   Record<string, unknown>;
  }) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => void;
  /** M6: Complete a password reset and auto-login. */
  resetPassword: (resetToken: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  /** Request a forgot-password email. Always succeeds generically on the API. */
  requestPasswordReset: (email: string) => Promise<{ ok: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/* FNV-1a-ish dual-pass hash for the per-user mailId. Same shape as the
   v28.14 buildMailId(); deterministic + opaque. Used by mailbox routing. */
function buildMailId(email: string, role: string): string {
  const s = `${email.toLowerCase()}::${role}::lc-pepper-v1`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (h >>> 0).toString(16).padStart(8, "0");
  return "u_" + hex + hex;
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(p => p[0]!.toUpperCase())
    .slice(0, 2)
    .join("") || "?";
}

/* Hydrate the ShellUser shape from a Supabase user + their profile row. */
function buildShellUser(supaUser: User, profile: Profile | null, companyId: string | null, subscriptionTier: "free" | "pro" = "free"): ShellUser {
  const email = supaUser.email || (profile?.email ?? "");
  const role  = (profile?.role ?? "coordinator") as Role;
  const name  = profile?.name || (typeof supaUser.user_metadata?.name === "string" ? supaUser.user_metadata.name : email);
  return {
    id:           supaUser.id,
    email,
    name,
    role,
    company:      profile?.company || "",
    companyId,
    mailId:       buildMailId(email, role),
    initials:     deriveInitials(name),
    gdprAccepted: true, /* the wizard's confirm step gates on it, so by the time we get a profile it's accepted */
    avatarUrl:    profile?.avatar_url || null,
    coverUrl:     profile?.cover_url  || null,
    onboardingComplete: profile?.onboarding_complete === true,
    isAdmin: profile?.is_admin === true,
    adminRole: profile?.admin_role ?? (profile?.is_admin === true ? "master" : null),
    subscriptionTier,
  };
}

async function hydrateFromSession(session: Session): Promise<ShellUser | null> {
  const userId = session.user.id;
  try {
    /* v49.10.5 — race each query against a 4s timeout. If a query hangs
       (e.g. RLS recursion, bad policy, network stall), we'd rather fall
       back to a minimal user than leave status stuck on "loading"
       forever. The fallback at least lets the user into the shell;
       individual pages will surface their own data-load errors. */
    const timeout = <T,>(p: PromiseLike<T>, label: string): Promise<T> => {
      return Promise.race([
        Promise.resolve(p),
        new Promise<T>((_, rej) =>
          setTimeout(() => rej(new Error(`hydrate timeout: ${label}`)), 4000)),
      ]);
    };
    const [profileR, membershipsR] = await Promise.all([
      timeout(supabase.from("profiles").select("*").eq("id", userId).maybeSingle(), "profiles"),
      timeout(supabase.from("memberships").select("company_id").eq("user_id", userId).limit(1), "memberships"),
    ]);
    const profile     = (profileR as { data?: Profile | null })?.data ?? null;
    let memberships   = (membershipsR as { data?: Array<{ company_id: string }> | null })?.data ?? null;
    let companyId     = (memberships && memberships[0]?.company_id) || null;

    /* v49.10.7 — self-heal missing tenant.
       Some accounts (e.g. signups that ran before the GoTrue lock fix)
       have a profile but no memberships row because the
       create_company_for_user RPC was blocked by the SDK lock during
       signup. The RPC is idempotent (re-uses an existing owner
       membership for the same name+kind) so it's safe to retry on
       every sign-in until it succeeds. */
    if (!companyId && profile?.company) {
      lcTrace("self-heal:start", { companyName: profile.company, role: profile.role });
      const kind = profile.role === "coordinator" ? "coordinator" : profile.role === "partner" ? "partner" : "hybrid";
      try {
        const newCompanyId = await LABOURCORE_API.companies.createForUser(profile.company, kind);
        lcTrace("self-heal:done", { newCompanyId });
        if (newCompanyId) companyId = newCompanyId;
      } catch (healErr) {
        lcTrace("self-heal:failed", { error: (healErr as Error).message });
      }
    }

    if (companyId) LABOURCORE_API.setActiveCompanyId(companyId);

    /* v50.335 — Defence P9: prime the learned match-model cache once the
       viewer's company is known. Fire-and-forget — scoring falls back to
       the deterministic heuristic until (and unless) it resolves. */
    if (companyId) primeMatchModel(companyId);

    /* v50.336 — Defence P10: hydrate this account's saved match weights
       (match_preferences) into the local cache. Fire-and-forget; the
       device-local weights serve until (and unless) the row arrives. */
    primeMatchWeights(userId);

    /* v50.72 — pull the company's subscription_tier so the shell can
       gate Pro-only features. One extra roundtrip but the row is
       small and RLS will let any member read it. Falls back to 'free'
       on any error so the gate fails closed. */
    let tier: "free" | "pro" = "free";
    if (companyId) {
      try {
        const tierR = await timeout(
          supabase.from("companies").select("subscription_tier").eq("id", companyId).maybeSingle(),
          "company-tier",
        );
        const raw = (tierR as { data?: { subscription_tier?: string } | null })?.data?.subscription_tier;
        if (raw === "pro") tier = "pro";
      } catch (e) {
        console.warn("[AuthContext] tier fetch failed (defaulting free):", e);
      }
    }

    return buildShellUser(session.user, profile, companyId, tier);
  } catch (e) {
    console.warn("[AuthContext] hydrate failed (using fallback user):", e);
    return buildShellUser(session.user, null, null);
  }
}

/* v49.10.5 — auth trace ring buffer (accessible at window.__lcAuthTrace).
   Captures every auth lifecycle event so we can diagnose "token landed
   but status didn't flip" failures by pasting the trace in DevTools. */
declare global {
  interface Window { __lcAuthTrace?: Array<{ t: number; e: string; data?: unknown }>; }
}
function lcTrace(e: string, data?: unknown) {
  if (typeof window === "undefined") return;
  window.__lcAuthTrace = window.__lcAuthTrace || [];
  window.__lcAuthTrace.push({ t: Date.now(), e, data });
  if (window.__lcAuthTrace.length > 100) window.__lcAuthTrace.shift();
  console.log("[auth]", e, data ?? "");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null, rawSession: null });

  /* On mount + on every auth-state-change, re-hydrate. */
  useEffect(() => {
    let cancelled = false;
    lcTrace("provider-mount");

    (async () => {
      lcTrace("getSession:start");
      const { data } = await supabase.auth.getSession();
      const session = data?.session || null;
      lcTrace("getSession:done", { hasSession: !!session, userId: session?.user?.id });
      if (cancelled) { lcTrace("getSession:cancelled"); return; }
      if (!session) {
        setState({ status: "anonymous", user: null, rawSession: null });
        lcTrace("state→anonymous (no initial session)");
        return;
      }
      lcTrace("initial-hydrate:start");
      const user = await hydrateFromSession(session);
      lcTrace("initial-hydrate:done", { hasUser: !!user, role: user?.role, companyId: user?.companyId });
      if (!cancelled) {
        setState({ status: "authenticated", user, rawSession: session });
        lcTrace("state→authenticated (initial)");
      }
    })();

    /* v49.10.4 — defer DB hydrate to next tick.
       Supabase v2's GoTrue client holds an internal lock while
       onAuthStateChange callbacks run. Awaiting supabase.from(...)
       queries inside the callback can deadlock: the DB request waits
       on the lock that the callback itself is holding. Symptoms: token
       lands in localStorage after signIn, but status never flips to
       "authenticated" because hydrateFromSession never resolves.
       setTimeout(_, 0) lets the callback return, releases the lock,
       then the DB queries can proceed. */
    const { data: sub } = supabase.auth.onAuthStateChange((event: string, session: Session | null) => {
      lcTrace("onAuthStateChange", { event, hasSession: !!session, userId: session?.user?.id });
      if (cancelled) { lcTrace("onAuthStateChange:cancelled"); return; }
      if (event === "SIGNED_OUT" || !session) {
        setState({ status: "anonymous", user: null, rawSession: null });
        lcTrace("state→anonymous (event=" + event + ")");
        return;
      }
      setTimeout(async () => {
        if (cancelled) { lcTrace("deferred-hydrate:cancelled-before-start"); return; }
        lcTrace("deferred-hydrate:start");
        const user = await hydrateFromSession(session);
        lcTrace("deferred-hydrate:done", { hasUser: !!user, role: user?.role, companyId: user?.companyId });
        if (!cancelled) {
          setState({ status: "authenticated", user, rawSession: session });
          lcTrace("state→authenticated (signed-in)");
        } else {
          lcTrace("deferred-hydrate:cancelled-after");
        }
      }, 0);
    });

    /* Listen for the legacy lc-auth-changed event too (fired by
       shellSignOut + any v48-era component that mutates auth state). */
    const onLegacy = () => {
      supabase.auth.getSession().then((res: { data: { session: Session | null } }) => {
        if (cancelled) return;
        if (!res?.data?.session) setState({ status: "anonymous", user: null, rawSession: null });
      });
    };
    window.addEventListener("lc-auth-changed", onLegacy);

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
      window.removeEventListener("lc-auth-changed", onLegacy);
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    async signIn(email, password) {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          // M6: detect reset_required from the shim's special error code
          if (error.message === "PASSWORD_RESET_REQUIRED" && data?.user) {
            const u = data.user as any;
            return {
              ok: false,
              resetRequired: true,
              resetToken: u.__reset_token,
              resetUser: { id: u.id, email: u.email },
            };
          }
          return { ok: false, error: error.message };
        }
        if (!data?.user) return { ok: false, error: "Sign-in returned no user" };
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async requestPasswordReset(email: string) {
      try {
        const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
        const res = await fetch(`${BASE_URL}/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
        // API always returns generic 200 when valid; treat network/5xx as soft fail.
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const json = await res.json();
            if (json?.error) msg = typeof json.error === "string" ? json.error : msg;
          } catch { /* ignore */ }
          return { ok: false, error: msg };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async resetPassword(resetToken: string, newPassword: string) {
      try {
        const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
        const res = await fetch(`${BASE_URL}/auth/reset-password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${resetToken}`,
          },
          body: JSON.stringify({ new_password: newPassword }),
        });
        const json = await res.json();
        if (!res.ok) return { ok: false, error: json.error || `HTTP ${res.status}` };
        // Reset succeeded — auto-login with the returned tokens
        if (json.access_token && json.refresh_token) {
          const { setTokens } = await import("@/lib/api/client");
          setTokens(json.access_token, json.refresh_token);
          const { _setCachedUser } = await import("@/lib/api/supabase-client");
          const user = {
            id: json.user?.id || "",
            email: json.user?.email || "",
            user_metadata: {},
          };
          const session = { access_token: json.access_token, refresh_token: json.refresh_token, user };
          _setCachedUser(user, session);
          // Fire auth state change to trigger hydration
          window.dispatchEvent(new Event("lc:auth-changed"));
          window.dispatchEvent(new Event("lc-auth-changed"));
          // Re-trigger AuthContext hydration
          const { data: sessData } = await supabase.auth.getSession();
          if (sessData?.session) {
            const hydratedUser = await hydrateFromSession(sessData.session as any);
            if (hydratedUser) {
              setState({ status: "authenticated", user: hydratedUser, rawSession: sessData.session as any });
            }
          }
          return { ok: true };
        }
        return { ok: false, error: "Reset succeeded but no tokens returned" };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async signUp({ email, password, name, role, company, extras }) {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name, role, company } },
        });
        if (error) return { ok: false, error: error.message };
        const supaUser = data?.user;
        if (!supaUser) return { ok: false, error: "Sign-up returned no user" };

        /* v48.277 — profile row. Wrapped in try/catch so a transient RLS
           issue doesn't block the user from completing registration. */
        try {
          const ex = extras || {};
          await supabase.from("profiles").upsert({
            id:                 supaUser.id,
            email,
            name,
            role,
            company,
            title:              role === "coordinator" ? "MEP Project Manager" : "",
            location:           "London",
            trades:             Array.isArray(ex.trades)        ? ex.trades        : null,
            crew_size:          ex.teamSize != null ? Number(ex.teamSize) : null,
            regions:            Array.isArray(ex.regions)       ? ex.regions       : null,
            declared_certs:     Array.isArray(ex.declaredCerts) ? ex.declaredCerts : null,
            typical_day_rate:   ex.typicalDayRate   != null ? Number(ex.typicalDayRate)   : null,
            project_types:      Array.isArray(ex.projectTypes)  ? ex.projectTypes  : null,
            typical_headcount:  ex.typicalHeadcount != null ? Number(ex.typicalHeadcount) : null,
            procurement_system: ex.procurementSystem || null,
          });
        } catch (profileErr) {
          console.warn("[signUp] profile upsert failed (non-fatal):", profileErr);
        }

        /* v48.274 — atomic company + owner-membership RPC. */
        try {
          const kind = role === "coordinator" ? "coordinator" : role === "partner" ? "partner" : "hybrid";
          const newCompanyId = await LABOURCORE_API.companies.createForUser(company, kind);
          if (newCompanyId) console.log("[signUp] tenant created:", newCompanyId, "kind:", kind);
        } catch (coErr) {
          console.warn("[signUp] company RPC failed (non-fatal):", coErr);
        }

        /* v48.277 — gdpr_consents row (data_processing grant). */
        try {
          await supabase.from("gdpr_consents").insert({
            user_id:        supaUser.id,
            consent_kind:   "data_processing",
            granted:        true,
            policy_version: "1.1",
          });
        } catch (consentErr) {
          console.warn("[signUp] gdpr_consents insert failed (non-fatal):", consentErr);
        }

        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    signOut: shellSignOut,
  }), [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/* Most components just want the user object, not the full mutation surface. */
export function useShellUser(): ShellUser | null {
  return useAuth().user;
}
